import React, { useState } from 'react';
import { Product, Category, Customer } from '../types';
import { Plus, Trash2, Tag, ShoppingBag, Users, Pencil, Save, X } from 'lucide-react';
import { saveProduct, deleteProduct, saveCategory, deleteCategory, saveCustomer, deleteCustomer } from '../services/storageService';
import { formatPhoneNumber } from '../utils';

interface AdminProps {
  products: Product[];
  categories: Category[];
  customers: Customer[];
  refreshData: () => Promise<void>;
}

const Admin: React.FC<AdminProps> = ({ products, categories, customers, refreshData }) => {
  const [activeTab, setActiveTab] = useState<'products' | 'categories' | 'customers'>('products');

  // Product Form State
  const [pName, setPName] = useState('');
  const [pCat, setPCat] = useState('');

  // Category Form State
  const [cName, setCName] = useState('');

  // Customer Edit State
  const [editingCustId, setEditingCustId] = useState<string | null>(null);
  const [editCustForm, setEditCustForm] = useState({ firstName: '', lastName: '', phone: '' });

  // --- Product Handlers ---
  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pName || !pCat) return;

    try {
      await saveProduct({
          id: crypto.randomUUID(),
          name: pName,
          description: '', // Default empty description
          price: 0, // Default price of 0
          categoryId: pCat, // Use categoryId instead of category
          isAvailable: true // Default to available
      });
      setPName('');
      await refreshData();
    } catch (error) {
      console.error('Error adding product:', error);
      alert('Failed to add product. Database connection may be unavailable.');
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if(confirm('Delete this product?')) {
        try {
            await deleteProduct(id);
            await refreshData();
        } catch (error) {
            console.error('Error deleting product:', error);
            alert('Failed to delete product. Database connection may be unavailable.');
        }
    }
  };

  // --- Category Handlers ---
  const handleAddCategory = async (e: React.FormEvent) => {
      e.preventDefault();
      if(!cName) return;

      try {
          await saveCategory({
              id: crypto.randomUUID(),
              name: cName
          });
          setCName('');
          await refreshData();
      } catch (error) {
          console.error('Error adding category:', error);
          alert('Failed to add category. Database connection may be unavailable.');
      }
  }

  const handleDeleteCategory = async (id: string) => {
      if(confirm('Delete this category? Products in this category will lose their category association.')) {
          try {
              await deleteCategory(id);
              await refreshData();
          } catch (error) {
              console.error('Error deleting category:', error);
              alert('Failed to delete category. Database connection may be unavailable.');
          }
      }
  }

  // --- Customer Handlers ---
  const startEditCustomer = (customer: Customer) => {
      setEditingCustId(customer.id);
      setEditCustForm({
          firstName: customer.firstName,
          lastName: customer.lastName,
          phone: customer.phone.replace(/\D/g, '') // Store raw digits only
      });
  };

  const cancelEditCustomer = () => {
      setEditingCustId(null);
  };

  const saveEditCustomer = async () => {
      if (!editingCustId) return;
      const original = customers.find(c => c.id === editingCustId);
      if (original) {
          try {
              await saveCustomer({
                  ...original,
                  firstName: editCustForm.firstName,
                  lastName: editCustForm.lastName,
                  phone: editCustForm.phone
              });
              await refreshData();
              setEditingCustId(null);
          } catch (error) {
              console.error('Error saving customer:', error);
              alert('Failed to save customer. Database connection may be unavailable.');
          }
      }
  };

  const handleDeleteCustomer = async (id: string) => {
      if(confirm('Delete this customer? Order history will remain but customer record will be removed.')) {
          try {
              await deleteCustomer(id);
              await refreshData();
          } catch (error) {
              console.error('Error deleting customer:', error);
              alert('Failed to delete customer. Database connection may be unavailable.');
          }
      }
  };

  return (
    <div className="space-y-6">
        <h2 className="text-2xl font-bold text-gray-800">Admin Management</h2>

        {/* Tabs */}
        <div className="flex space-x-1 border-b border-gray-200 overflow-x-auto">
            <button 
                onClick={() => setActiveTab('products')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${activeTab === 'products' ? 'bg-white border border-b-0 border-gray-200 text-red-700' : 'text-gray-500 hover:text-gray-700'}`}
            >
                <div className="flex items-center"><ShoppingBag className="w-4 h-4 mr-2"/> Products</div>
            </button>
            <button 
                onClick={() => setActiveTab('categories')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${activeTab === 'categories' ? 'bg-white border border-b-0 border-gray-200 text-red-700' : 'text-gray-500 hover:text-gray-700'}`}
            >
               <div className="flex items-center"><Tag className="w-4 h-4 mr-2"/> Categories</div>
            </button>
            <button 
                onClick={() => setActiveTab('customers')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${activeTab === 'customers' ? 'bg-white border border-b-0 border-gray-200 text-red-700' : 'text-gray-500 hover:text-gray-700'}`}
            >
               <div className="flex items-center"><Users className="w-4 h-4 mr-2"/> Customers</div>
            </button>
        </div>

        <div className="bg-white p-6 rounded-b-lg rounded-r-lg shadow-sm border border-gray-200 border-t-0 mt-0">
            {/* Products Tab */}
            {activeTab === 'products' && (
                <div className="space-y-6">
                    {/* Add Product Form */}
                    <form onSubmit={handleAddProduct} className="flex gap-4 items-end bg-gray-50 p-4 rounded-lg">
                        <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Product Name</label>
                            <input type="text" value={pName} onChange={e => setPName(e.target.value)} className="w-full p-2 border border-gray-300 rounded text-sm" required />
                        </div>
                        <div className="w-1/3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                            <select value={pCat} onChange={e => setPCat(e.target.value)} className="w-full p-2 border border-gray-300 rounded text-sm" required>
                                <option value="">Select...</option>
                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <button type="submit" className="bg-red-700 text-white px-4 py-2 rounded text-sm hover:bg-red-800 flex items-center">
                            <Plus className="w-4 h-4 mr-1" /> Add
                        </button>
                    </form>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {products.map(p => (
                                    <tr key={p.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{p.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <span className="bg-gray-100 px-2 py-1 rounded-full text-xs">{p.category}</span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button onClick={() => handleDeleteProduct(p.id)} className="text-red-600 hover:text-red-900 bg-red-50 p-2 rounded-full">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Categories Tab */}
            {activeTab === 'categories' && (
                <div className="space-y-6">
                     <form onSubmit={handleAddCategory} className="flex gap-4 items-end bg-gray-50 p-4 rounded-lg">
                        <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Category Name</label>
                            <input type="text" value={cName} onChange={e => setCName(e.target.value)} className="w-full p-2 border border-gray-300 rounded text-sm" required />
                        </div>
                        <button type="submit" className="bg-red-700 text-white px-4 py-2 rounded text-sm hover:bg-red-800 flex items-center">
                            <Plus className="w-4 h-4 mr-1" /> Add
                        </button>
                    </form>

                     <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {categories.map(c => (
                            <li key={c.id} className="bg-white border border-gray-200 p-4 rounded-lg flex justify-between items-center shadow-sm">
                                <span className="font-medium text-gray-800">{c.name}</span>
                                <button onClick={() => handleDeleteCategory(c.id)} className="text-gray-400 hover:text-red-600 p-1">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Customers Tab */}
            {activeTab === 'customers' && (
                <div className="space-y-6">
                    <p className="text-sm text-gray-500">Manage customer details. Stats are updated automatically based on orders.</p>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orders</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {customers.map(c => (
                                    <tr key={c.id} className="hover:bg-gray-50">
                                        {editingCustId === c.id ? (
                                            <>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <input 
                                                        className="border border-gray-300 rounded p-1 text-sm w-full"
                                                        value={editCustForm.firstName}
                                                        onChange={(e) => setEditCustForm({...editCustForm, firstName: e.target.value})}
                                                    />
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                     <input 
                                                        className="border border-gray-300 rounded p-1 text-sm w-full"
                                                        value={editCustForm.lastName}
                                                        onChange={(e) => setEditCustForm({...editCustForm, lastName: e.target.value})}
                                                    />
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                     <input 
                                                        className="border border-gray-300 rounded p-1 text-sm w-full"
                                                        value={formatPhoneNumber(editCustForm.phone)}
                                                        onChange={(e) => {
                                                            // Store raw digits only
                                                            const rawDigits = e.target.value.replace(/\D/g, '');
                                                            setEditCustForm({...editCustForm, phone: rawDigits});
                                                        }}
                                                    />
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {c.totalOrders}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex justify-end space-x-2">
                                                        <button onClick={saveEditCustomer} className="text-green-600 hover:text-green-900 bg-green-50 p-2 rounded-full">
                                                            <Save className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={cancelEditCustomer} className="text-gray-600 hover:text-gray-900 bg-gray-100 p-2 rounded-full">
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{c.firstName}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{c.lastName}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatPhoneNumber(c.phone)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{c.totalOrders}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex justify-end space-x-2">
                                                        <button onClick={() => startEditCustomer(c)} className="text-blue-600 hover:text-blue-900 bg-blue-50 p-2 rounded-full">
                                                            <Pencil className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={() => handleDeleteCustomer(c.id)} className="text-red-600 hover:text-red-900 bg-red-50 p-2 rounded-full">
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    </div>
  );
};

export default Admin;
